import type { ReactNode } from 'react';
import { CalendarCheck, ClipboardList, ReceiptIndianRupee, ShieldCheck, Sparkles } from 'lucide-react';
import { Logo } from '@/components/ui/Logo';
import { env } from '@/config/env';
import { ThemeToggle } from '@/components/ThemeToggle';

const HIGHLIGHTS = [
  {
    icon: CalendarCheck,
    title: 'Morning bell to last period',
    body: 'Registers marked, timetables set and cover arranged before the break.',
  },
  {
    icon: ClipboardList,
    title: 'Answer sheet to report card',
    body: 'Marks become grades, ranks and printed cards — no calculator, no re-typing.',
  },
  {
    icon: ReceiptIndianRupee,
    title: 'Every rupee accounted for',
    body: 'Structures, installments, receipts and dues that reconcile themselves.',
  },
  {
    icon: ShieldCheck,
    title: 'The quiet signals',
    body: 'A slipping attendance or a missed exam, noticed while it can still be fixed.',
  },
];

export function AuthLayout({ children }: { children: ReactNode }) {
  return (
    <div className="min-h-dvh bg-paper lg:grid lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)]">
      {/* ---------------- Brand Panel ---------------- */}
      {/*
        Painted in the product's own chrome tokens — `frame`/`lock` — rather
        than a colour of its own, so the sign-in screen is the same dark navy as
        every console's chrome instead of a green that appears nowhere else.
        Both tokens are dark in either theme, which is what lets the white type
        on top of them stay readable without a single `dark:` prefix (§7).
      */}
      <aside className="relative hidden flex-col justify-between overflow-hidden bg-gradient-to-br from-frame via-lock to-frame p-10 lg:flex xl:p-14 text-white">
        {/* Glow ambient background accents.
            Drawn from Tailwind's indigo — the nearest scale to `--accent` — and
            the amber the product already uses for its gold, because an opacity
            modifier on a `var()` colour compiles to nothing in this setup, so a
            `bg-accent/20` orb would simply not be there. */}
        <div
          className="pointer-events-none absolute -right-24 -top-24 h-96 w-96 rounded-full bg-indigo-500/25 blur-3xl"
          aria-hidden
        />
        <div
          className="pointer-events-none absolute -bottom-32 -left-20 h-96 w-96 rounded-full bg-amber-400/15 blur-3xl"
          aria-hidden
        />

        <div className="relative z-10">
          <Logo tone="light" subtitle="Smart institute management" />
        </div>

        <div className="relative z-10 max-w-lg">
          <span className="inline-flex items-center gap-2 rounded-pill bg-white/10 px-3.5 py-1.5 text-xs font-semibold text-indigo-100 backdrop-blur-md border border-white/15 mb-4 shadow-sm">
            <Sparkles className="h-3.5 w-3.5 text-indigo-300" /> Premium Institute Platform
          </span>

          <h1 className="text-3xl xl:text-4xl font-extrabold leading-tight text-white tracking-tight">
            The institute day,
            <span className="block text-amber">written down once.</span>
          </h1>

          <p className="mt-3.5 text-sm leading-relaxed text-white/75 font-normal">
            A register marked in class is a fee clerk's record, a principal's report and a parent's
            update — the same minute, without anyone copying it twice.
          </p>

          <ul className="mt-8 flex flex-col gap-3.5">
            {HIGHLIGHTS.map(({ icon: Icon, title, body }) => (
              <li
                key={title}
                className="flex items-start gap-3.5 p-3.5 rounded-[14px] bg-white/[0.06] backdrop-blur-md border border-white/10 shadow-2xs hover:border-white/20 transition-colors"
              >
                <span
                  className="mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-[10px] bg-gradient-to-br from-indigo-400/20 to-indigo-600/30 text-indigo-300 border border-indigo-400/30"
                  aria-hidden
                >
                  <Icon className="h-4.5 w-4.5" strokeWidth={1.9} />
                </span>
                <div>
                  <span className="block text-sm font-bold text-white">{title}</span>
                  <span className="block text-xs text-white/70 mt-0.5 font-medium">{body}</span>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <div className="relative z-10 flex items-center gap-3">
          <span className="rounded-pill border border-white/20 bg-white/10 px-3 py-1 font-mono text-2xs font-bold uppercase tracking-wider text-indigo-100 backdrop-blur-md">
            Secure SSL Sign-in
          </span>
          <span className="h-px flex-1 bg-white/15" />
          
        </div>
      </aside>

      {/* ---------------- Form Panel ---------------- */}
      <main className="relative flex min-h-dvh flex-col px-4 py-6 sm:px-8 sm:py-8 lg:py-10">
        <header className="flex items-center justify-between gap-3">
          <Logo className="lg:invisible min-w-0" subtitle="Smart institute management" />
          <div className="shrink-0">
            <ThemeToggle />
          </div>
        </header>

        <div className="flex flex-1 items-center justify-center py-6 sm:py-10">
          <div className="w-full max-w-[420px] animate-fade-up">{children}</div>
        </div>

        <footer className="text-center text-xs text-ink-3 font-medium">
          © {new Date().getFullYear()} {env.appName}
        </footer>
      </main>
    </div>
  );
}

